import { useCallback, useEffect, useState } from "react";
import { LuCopyPlus } from "react-icons/lu";
import { PiBoxArrowUpFill } from "react-icons/pi";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroItemEstoque from "../../components/modais/ModalCadastroItemEstoque";
import ModalCadastroLocalEstoque from "../../components/modais/ModalCadastroLocalEstoque";
import { api } from "../../services/api";
import {
  repositorioAcertoEstoque,
  repositorioEstoqueItem,
  repositorioLocalEstoque,
  repositorioUnidadeMedida,
} from "../../sync/repositories/almoxarifado";

const AJUDA = {
  titulo: "Registro de Acerto de Estoque",
  descricao:
    "Use esta tela para corrigir manualmente o saldo de um item — por perda, quebra, inventário físico ou qualquer diferença entre o sistema e a contagem real. Cada acerto registrado aqui gera automaticamente uma Movimentação de Estoque com a referência 'ACERTO DE ESTOQUE', visível na tela de Movimentações.",
  secoes: [
    {
      titulo: "Dados do Acerto",
      campos: [
        {
          nome: "Item",
          obrigatorio: true,
          explicacao:
            "O item de estoque (cadastrado em Itens de Estoque) que está sendo ajustado. Ao selecionar, o card 'Estoque Atual' mostra o saldo de hoje.",
        },
        {
          nome: "Local",
          obrigatorio: true,
          explicacao:
            "O Local de Estoque onde o ajuste está sendo feito.",
        },
        {
          nome: "Tipo de Ajuste",
          obrigatorio: true,
          explicacao:
            "Entrada soma quantidade ao saldo, Saída subtrai, e Correção deixa você digitar direto o saldo final desejado — o sistema calcula a diferença automaticamente.",
        },
        {
          nome: "Quantidade",
          obrigatorio: true,
          explicacao:
            "Quanto entra ou sai do estoque. Aparece apenas quando o Tipo de Ajuste é Entrada ou Saída — não é usado no modo Correção.",
        },
        {
          nome: "Novo Saldo Desejado",
          obrigatorio: true,
          explicacao:
            "Aparece apenas no modo Correção: informe o saldo final que deveria existir. O sistema calcula a diferença em relação ao saldo atual e registra automaticamente como entrada ou saída.",
        },
        {
          nome: "Motivo do Ajuste",
          obrigatorio: true,
          explicacao:
            "Justificativa do ajuste (ex.: 'Quebra', 'Perda', 'Inventário') — fica registrada no histórico para consulta futura.",
        },
        {
          nome: "Observação",
          obrigatorio: false,
          explicacao: "Detalhes adicionais sobre o ajuste, se necessário.",
        },
      ],
    },
  ],
  dicas: [
    "Registrar um acerto exige conexão com a internet — diferente de outros cadastros do sistema, esta tela não funciona no modo offline, pois grava o acerto direto no servidor (que já cria a movimentação de estoque correspondente automaticamente).",
    "Depois de salvo, o acerto não pode ser editado ou excluído por esta tela — se precisar reverter, registre um novo acerto compensando a diferença.",
  ],
};

export default function GestaoAcertoEstoque() {
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [acertos, setAcertos] = useState([]);
  const [acertosFiltrados, setAcertosFiltrados] = useState([]);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  // Dados para os selects
  const [itens, setItens] = useState([]);
  const [locais, setLocais] = useState([]);
  const [unidadesMedida, setUnidadesMedida] = useState([]);
  const [mostrarModalItem, setMostrarModalItem] = useState(false);
  const [mostrarModalLocal, setMostrarModalLocal] = useState(false);

  // Estoque atual do item selecionado
  const [estoqueAtual, setEstoqueAtual] = useState(null);
  const [buscandoEstoque, setBuscandoEstoque] = useState(false);
  

  const [filtros, setFiltros] = useState({
    item: "",
    local: "",
    tipo: "",
    data_inicio: "",
    data_fim: ""
  });

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);
  
  const [formData, setFormData] = useState({
    item: "",
    local: "",
    tipo_ajuste: "entrada",
    quantidade: "",
    novo_saldo: "",
    motivo: "",
    observacao: "",
  });

  const tiposAjuste = [
    { value: "entrada", label: "Entrada (Adicionar)", cor: "bg-green-100 text-green-800" },
    { value: "saida", label: "Saída (Remover)", cor: "bg-red-100 text-red-800" },
    { value: "correcao", label: "Correção (Definir valor exato)", cor: "bg-blue-100 text-blue-800" },
  ];

  const buscarEstoqueAtual = useCallback((itemId) => {
    if (!itemId) {
      setEstoqueAtual(null);
      return;
    }

    const itemSelecionado = itens.find(
      (item) => String(item.id) === String(itemId)
    );

    const saldo = Number(itemSelecionado?.saldo_atual || itemSelecionado?.quantidade || 0);

    setEstoqueAtual(saldo);
    return saldo;
  }, [itens]);

  useEffect(() => {
    if (formData.item) {
      buscarEstoqueAtual(formData.item);
    } else {
      setEstoqueAtual(null);
    }
  }, [formData.item, buscarEstoqueAtual]);

  // Quando tipo_ajuste mudar, limpar campos relacionados
  useEffect(() => {
    if (formData.tipo_ajuste === "correcao") {
      setFormData(prev => ({ ...prev, quantidade: "", novo_saldo: "" }));
    } else {
      setFormData(prev => ({ ...prev, novo_saldo: "" }));
    }
  }, [formData.tipo_ajuste]);

  // Calcular diferença quando o usuário digita o novo saldo (modo correção)
  const handleNovoSaldoChange = (e) => {
    const novoSaldo = e.target.value;
    setFormData(prev => ({ ...prev, novo_saldo: novoSaldo }));
    
    if (estoqueAtual !== null && novoSaldo !== "") {
      const diferenca = Number(novoSaldo) - estoqueAtual;
      setFormData(prev => ({ 
        ...prev, 
        quantidade: Math.abs(diferenca).toString(),
        tipo_ajuste: diferenca >= 0 ? "entrada" : "saida"
      }));
    }
  };

  // Carregar itens
  const carregarItens = useCallback(async () => {
    try {
      const response = await repositorioEstoqueItem.listar();
      const dados = response.data.results || response.data;
      setItens(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar itens:", error);
      setItens([]);
    }
  }, []);

  // Carregar locais
  const carregarLocais = useCallback(async () => {
    try {
      const response = await repositorioLocalEstoque.listar();
      const dados = response.data.results || response.data;
      setLocais(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar locais:", error);
      setLocais([]);
    }
  }, []);

  // Carregar unidades de medida (necessário só pelo modal de cadastro rápido de Item)
  const carregarUnidadesMedida = useCallback(async () => {
    try {
      const response = await repositorioUnidadeMedida.listar();
      const dados = response.data.results || response.data;
      setUnidadesMedida(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar unidades de medida:", error);
      setUnidadesMedida([]);
    }
  }, []);

  // Carregar acertos de estoque
  const carregarAcertos = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioAcertoEstoque.listar();
      const dados = response.data.results || response.data;
      setAcertos(Array.isArray(dados) ? dados : []);
      setAcertosFiltrados(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar acertos:", error);
      alert("Erro ao carregar lista de acertos");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarAcertos();
    carregarItens();
    carregarLocais();
    carregarUnidadesMedida();
  }, [carregarAcertos, carregarItens, carregarLocais, carregarUnidadesMedida]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.item) {
      alert("Selecione o item.");
      return false;
    }
    if (!formData.local) {
      alert("Selecione o local.");
      return false;
    }
    
    if (formData.tipo_ajuste === "correcao") {
      if (!formData.novo_saldo || Number(formData.novo_saldo) < 0) {
        alert("Informe o novo saldo desejado (valor maior ou igual a zero).");
        return false;
      }
      if (estoqueAtual === null) {
        alert("Não foi possível consultar o estoque atual. Tente novamente.");
        return false;
      }
    } else {
      if (!formData.quantidade || Number(formData.quantidade) <= 0) {
        alert("Informe uma quantidade válida.");
        return false;
      }
    }
    
    if (!formData.motivo.trim()) {
      alert("Informe o motivo do ajuste.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    let quantidadeFinal = Number(formData.quantidade);
    let tipoAjusteFinal = formData.tipo_ajuste;
    let diferenca = null;

    // Se for correção, calcular a diferença
    if (formData.tipo_ajuste === "correcao") {
      diferenca = Number(formData.novo_saldo) - estoqueAtual;
      quantidadeFinal = Math.abs(diferenca);
      tipoAjusteFinal = diferenca >= 0 ? "entrada" : "saida";
      
      if (diferenca === 0) {
        alert("O novo saldo é igual ao atual. Nenhum ajuste necessário.");
        setLoading(false);
        return;
      }
    }

    const payload = {
      item: formData.item,
      local: formData.local,
      tipo_ajuste: tipoAjusteFinal,
      quantidade: quantidadeFinal,
      motivo: formData.motivo,
      observacao: formData.tipo_ajuste === "correcao" 
        ? `Correção de estoque: Saldo anterior: ${estoqueAtual}, Novo saldo: ${formData.novo_saldo}. ${formData.observacao || ""}`
        : formData.observacao || "",
    };

    try {
      // Registrar o acerto de estoque — o backend (AcertoEstoqueViewSet.perform_create)
      // já cria a movimentação de estoque correspondente automaticamente, então não
      // é preciso (e não deve) chamar /almoxarifado/movimentacoes-estoque/ aqui também.
      await api.post("/almoxarifado/acertos/", payload);

      const mensagem = formData.tipo_ajuste === "correcao"
        ? `Correção realizada com sucesso! Diferença: ${diferenca > 0 ? "+" : ""}${diferenca} unidades.`
        : "Acerto de estoque registrado com sucesso!";
      
      alert(mensagem);
      handleCancel();
      carregarAcertos();
      carregarItens(); // Recarrega para atualizar saldos
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNovoAcerto = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      item: "",
      local: "",
      tipo_ajuste: "entrada",
      quantidade: "",
      novo_saldo: "",
      motivo: "",
      observacao: "",
    });
    setEstoqueAtual(null);
    setMostrarFormulario(false);
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(acertos) ? [...acertos] : [];
    
    if (filtros.item) {
      filtrados = filtrados.filter(a => String(a.item) === filtros.item);
    }
    
    if (filtros.local) {
      filtrados = filtrados.filter(a => String(a.local) === filtros.local);
    }
    
    if (filtros.tipo) {
      filtrados = filtrados.filter(a => a.tipo_ajuste === filtros.tipo);
    }
    
    if (filtros.data_inicio) {
      filtrados = filtrados.filter(a => a.data_acerto >= filtros.data_inicio);
    }
    
    if (filtros.data_fim) {
      filtrados = filtrados.filter(a => a.data_acerto <= filtros.data_fim);
    }
    
    setAcertosFiltrados(filtrados);
    setPaginaAtual(1);
  };

  const limparFiltros = () => {
    setFiltros({ item: "", local: "", tipo: "", data_inicio: "", data_fim: "" });
    setAcertosFiltrados(acertos);
    setPaginaAtual(1);
  };

  const formatarData = (data) => {
    if (!data) return "—";
    return new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const formatarQuantidade = (quantidade) => {
    if (!quantidade && quantidade !== 0) return "—";
    return Number(quantidade).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  };

  const getTipoInfo = (tipo) => {
    const tipoInfo = tiposAjuste.find(t => t.value === tipo);
    return tipoInfo || { label: tipo, cor: "bg-gray-100 text-gray-800" };
  };

  const getItemNome = (itemId) => {
    const item = itens.find(i => String(i.id) === String(itemId));
    return item?.nome || itemId;
  };

  const getLocalNome = (localId) => {
    const local = locais.find(l => String(l.id) === String(localId));
    return local?.nome || localId;
  };

  // Paginação
  const indiceUltimoItem = paginaAtual * itensPorPagina;
  const indicePrimeiroItem = indiceUltimoItem - itensPorPagina;
  const acertosPaginados = acertosFiltrados.slice(indicePrimeiroItem, indiceUltimoItem);
  const totalPaginas = Math.ceil(acertosFiltrados.length / itensPorPagina);

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Acerto de Estoque <PiBoxArrowUpFill size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoAcerto}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Acerto
            </button>
          )}
        </div>
        
        {/* Formulário de Acerto - Layout otimizado com mais colunas */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                Registrar Acerto de Estoque
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Grid com 4 colunas em telas grandes, 2 em médias */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="flex flex-col min-w-0">
                    <label className="text-sm font-medium text-gray-700 mb-1">Item *</label>

                    <div className="flex items-center gap-2">
                      <select
                        name="item"
                        value={formData.item}
                        onChange={handleChange}
                        className="w-full flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione...</option>
                        {itens.map(i => (
                          <option key={i.id} value={i.id}>{i.nome}</option>
                        ))}
                      </select>

                      <button
                        type="button"
                        onClick={() => setMostrarModalItem(true)}
                        title="Cadastrar novo item de estoque"
                        className="text-green-600 hover:text-green-800"
                      >
                        <LuCopyPlus size={22} />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col min-w-0">
                    <label className="text-sm font-medium text-gray-700 mb-1">Local *</label>

                    <div className="flex items-center gap-2">
                      <select
                        name="local"
                        value={formData.local}
                        onChange={handleChange}
                        className="w-full flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione...</option>
                        {locais.map(l => (
                          <option key={l.id} value={l.id}>{l.nome}</option>
                        ))}
                      </select>

                      <button
                        type="button"
                        onClick={() => setMostrarModalLocal(true)}
                        title="Cadastrar novo local de estoque"
                        className="text-green-600 hover:text-green-800"
                      >
                        <LuCopyPlus size={22} />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Tipo de Ajuste *</label>
                    <select
                      name="tipo_ajuste"
                      value={formData.tipo_ajuste}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 text-sm"
                      required
                    >
                      {tiposAjuste.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Card de Estoque Atual */}
                  <div className="flex flex-col bg-gray-50 rounded-lg p-2 border border-gray-200">
                    <label className="text-sm font-medium text-gray-700 mb-1">Estoque Atual</label>
                    <div className="flex items-center justify-between">
                      {buscandoEstoque ? (
                        <span className="text-sm text-gray-500">Consultando...</span>
                      ) : estoqueAtual !== null ? (
                        <span className="text-xl font-bold text-blue-600">
                          {formatarQuantidade(estoqueAtual)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">
                          {formData.item && formData.local ? "Carregando..." : "Selecione item e local"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Segunda linha - campos dinâmicos baseados no tipo */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {formData.tipo_ajuste === "correcao" ? (
                    <>
                      <div className="flex flex-col lg:col-span-2">
                        <label className="text-sm font-medium text-gray-700 mb-1">
                          Novo Saldo Desejado *
                        </label>
                        <input
                          type="number"
                          step="0.001"
                          name="novo_saldo"
                          value={formData.novo_saldo}
                          onChange={handleNovoSaldoChange}
                          className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 text-sm"
                          placeholder="Digite o saldo final desejado"
                          required
                        />
                        {formData.novo_saldo && estoqueAtual !== null && (
                          <p className="text-xs mt-1">
                            Diferença: <span className={Number(formData.novo_saldo) - estoqueAtual >= 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                              {Number(formData.novo_saldo) - estoqueAtual >= 0 ? "+" : ""}
                              {formatarQuantidade(Number(formData.novo_saldo) - estoqueAtual)}
                            </span>
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col lg:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Quantidade *
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        name="quantidade"
                        value={formData.quantidade}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 text-sm"
                        placeholder="0,000"
                        required
                      />
                    </div>
                  )}

                  <div className="flex flex-col lg:col-span-2">
                    <label className="text-sm font-medium text-gray-700 mb-1">Motivo do Ajuste *</label>
                    <input
                      type="text"
                      name="motivo"
                      value={formData.motivo}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 text-sm"
                      placeholder="Ex: Quebra, Perda, Inventário..."
                      required
                    />
                  </div>
                </div>

                {/* Terceira linha - Observação */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Observação</label>
                    <textarea
                      name="observacao"
                      value={formData.observacao}
                      onChange={handleChange}
                      rows={2}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 text-sm"
                      placeholder="Informações adicionais sobre o ajuste..."
                    />
                  </div>
                </div>

                {/* Aviso */}
                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                  <p className="text-xs text-yellow-800">
                    ⚠️ Atenção: Este acerto gerará uma movimentação de estoque com a referência "ACERTO DE ESTOQUE"
                    e poderá ser visualizada no relatório de movimentações.
                  </p>
                </div>

                {/* Botões */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300 text-sm"
                  >
                    {loading ? "Salvando..." : "Registrar Acerto"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors text-sm"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {/* Filtros e Tabela - continua igual, só adicionar coluna de diferença na tabela se quiser */}
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros - já está ok */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Item</label>
                  <select
                    value={filtros.item}
                    onChange={(e) => setFiltros({...filtros, item: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 text-sm"
                  >
                    <option value="">Todos os itens</option>
                    {itens.map(i => (
                      <option key={i.id} value={i.id}>{i.nome}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Local</label>
                  <select
                    value={filtros.local}
                    onChange={(e) => setFiltros({...filtros, local: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 text-sm"
                  >
                    <option value="">Todos os locais</option>
                    {locais.map(l => (
                      <option key={l.id} value={l.id}>{l.nome}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Tipo de Ajuste</label>
                  <select
                    value={filtros.tipo}
                    onChange={(e) => setFiltros({...filtros, tipo: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 text-sm"
                  >
                    <option value="">Todos os tipos</option>
                    {tiposAjuste.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Data Início</label>
                  <input
                    type="date"
                    value={filtros.data_inicio}
                    onChange={(e) => setFiltros({...filtros, data_inicio: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 text-sm"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                  <input
                    type="date"
                    value={filtros.data_fim}
                    onChange={(e) => setFiltros({...filtros, data_fim: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300 text-sm"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors text-sm"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors text-sm"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Acertos */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center flex-wrap gap-2">
                <p className="text-gray-600 text-sm">
                  {acertosFiltrados.length} acerto(s) encontrado(s)
                </p>
                <div className="text-sm text-gray-500">
                  Página {paginaAtual} de {totalPaginas || 1}
                </div>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Data</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Item</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Local</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Tipo</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Quantidade</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Motivo</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Observação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {acertosPaginados.map((acerto) => {
                          const tipoInfo = getTipoInfo(acerto.tipo_ajuste);
                          return (
                            <tr key={acerto.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                              <td className="p-3 text-sm">{formatarData(acerto.data_acerto)}</td>
                              <td className="p-3 text-sm font-medium">{getItemNome(acerto.item)}</td>
                              <td className="p-3 text-sm">{getLocalNome(acerto.local)}</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${tipoInfo.cor}`}>
                                  {tipoInfo.label}
                                </span>
                              </td>
                              <td className="p-3 text-sm text-right font-medium">{formatarQuantidade(acerto.quantidade)}</td>
                              <td className="p-3 text-sm">{acerto.motivo}</td>
                              <td className="p-3 text-sm max-w-xs truncate">{acerto.observacao || "—"}</td>
                            </tr>
                          );
                        })}
                        
                        {acertosPaginados.length === 0 && (
                          <tr>
                            <td colSpan="7" className="p-8 text-center text-gray-500 text-sm">
                              {filtros.item || filtros.local || filtros.tipo || filtros.data_inicio || filtros.data_fim
                                ? "Nenhum acerto encontrado com os filtros selecionados."
                                : "Nenhum acerto de estoque registrado."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginação */}
                  {totalPaginas > 1 && (
                    <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-center items-center gap-2">
                      <button
                        onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 1))}
                        disabled={paginaAtual === 1}
                        className={`px-3 py-1 rounded transition-colors text-sm ${paginaAtual === 1 ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                      >
                        Anterior
                      </button>
                      <div className="flex gap-1">
                        {Array.from({ length: Math.min(totalPaginas, 5) }, (_, i) => {
                          let num;
                          if (totalPaginas <= 5) num = i + 1;
                          else if (paginaAtual <= 3) num = i + 1;
                          else if (paginaAtual >= totalPaginas - 2) num = totalPaginas - 4 + i;
                          else num = paginaAtual - 2 + i;
                          return (
                            <button
                              key={num}
                              onClick={() => setPaginaAtual(num)}
                              className={`w-8 h-8 rounded transition-colors text-sm ${paginaAtual === num ? "bg-green-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                            >
                              {num}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas))}
                        disabled={paginaAtual === totalPaginas}
                        className={`px-3 py-1 rounded transition-colors text-sm ${paginaAtual === totalPaginas ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
                      >
                        Próxima
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <ModalCadastroItemEstoque
        isOpen={mostrarModalItem}
        onClose={() => setMostrarModalItem(false)}
        unidadesMedida={unidadesMedida}
        onSuccess={async (novo) => {
          await carregarItens();
          setFormData((prev) => ({ ...prev, item: novo.id }));
        }}
      />
      <ModalCadastroLocalEstoque
        isOpen={mostrarModalLocal}
        onClose={() => setMostrarModalLocal(false)}
        onSuccess={async (novo) => {
          await carregarLocais();
          setFormData((prev) => ({ ...prev, local: novo.id }));
        }}
      />
    </Layout>
  );
}